import React, { useState, useEffect } from "react";
import { Form, Button, Spinner, Modal } from "react-bootstrap";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../DoctorData/DoctorData.css";
import "./SpecialtiesManagement.css";
import { apiUrl } from "../../config/appConfig";

const SpecialtiesManagement = () => {
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSpecialtyName, setNewSpecialtyName] = useState("");
  const [addingSpecialty, setAddingSpecialty] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch specialties from API
  const fetchSpecialties = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        apiUrl('/specialties'),
        {
          headers: { accept: "application/json" },
        }
      );

      if (response.data.success && response.data.data?.specialties) {
        setSpecialties(response.data.data.specialties);
      } else {
        toast.error("Failed to load specialties");
        setSpecialties([]);
      }
    } catch (error) {
      console.error("Error fetching specialties:", error);
      toast.error("Failed to load specialties");
      setSpecialties([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecialties();
  }, []);

  // Handle adding new specialty
  const handleAddSpecialty = async (e) => {
    e.preventDefault();
    if (!newSpecialtyName.trim()) {
      toast.error("Please enter a specialty name");
      return;
    }

    setAddingSpecialty(true);
    try {
      const response = await axios.post(
        apiUrl('/specialties'),
        { name: newSpecialtyName.trim() },
        {
          headers: { 
            accept: "application/json",
            "Content-Type": "application/json"
          },
        }
      );

      if (response.data.success) {
        toast.success(`Specialty "${newSpecialtyName.trim()}" added successfully`);
        setNewSpecialtyName("");
        setShowAddModal(false);
        // Refresh the specialties list
        await fetchSpecialties();
        // Reset to first page to show the new specialty
        setPage(1);
      } else {
        toast.error(response.data.message || "Failed to add specialty");
      }
    } catch (error) {
      console.error("Error adding specialty:", error);
      const errorMessage = error.response?.data?.message || "Failed to add specialty";
      toast.error(errorMessage);
    } finally {
      setAddingSpecialty(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(specialties.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSpecialties = specialties.slice(startIndex, endIndex);

  return (
    <>
      <ToastContainer />
      <div className="apt-main-content" style={{ minHeight: "100vh" }}>
        <div className="innerWrapper">
          <div className="d-flex justify-content-between align-items-center mb-4 tblHdBlock">
            <h2 className="apt-section-title" style={{ margin: 0 }}>Specialties Management</h2>
            <Button
              variant="primary"
              onClick={() => setShowAddModal(true)}
              className="d-flex align-items-center gap-2"
            >
              <span>+</span> Add New Specialty
            </Button>
          </div>

          <div className="apt-appointments-section">
            <div className="apt-appointments-table">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Specialty Name</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={2} style={{ textAlign: "center", padding: "40px" }}>
                        <div className="d-flex justify-content-center align-items-center gap-2">
                          <Spinner animation="border" variant="primary" />
                          <span>Loading specialties...</span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedSpecialties.length > 0 ? (
                    paginatedSpecialties.map((specialty, idx) => (
                      <tr key={specialty}>
                        <td>{startIndex + idx + 1}</td>
                        <td>{specialty}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} style={{ textAlign: "center", padding: "40px" }}>
                        <span>No specialties found</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Specialty Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          if (!addingSpecialty) {
            setShowAddModal(false);
            setNewSpecialtyName("");
          }
        }}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton={!addingSpecialty}>
          <Modal.Title>Add New Specialty</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSpecialty}>
            <Form.Group className="mb-3">
              <Form.Label>Specialty Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter specialty name (e.g., Cardiac Surgery)"
                value={newSpecialtyName}
                onChange={(e) => setNewSpecialtyName(e.target.value)}
                required
                disabled={addingSpecialty}
                autoFocus
                minLength={2}
                maxLength={100}
              />
              <Form.Text className="text-muted">
                The specialty will be automatically sorted alphabetically. Duplicate specialties (case-insensitive) are not allowed.
              </Form.Text>
            </Form.Group>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setNewSpecialtyName("");
                }}
                disabled={addingSpecialty}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={addingSpecialty}>
                {addingSpecialty ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Adding...
                  </>
                ) : (
                  "Add Specialty"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SpecialtiesManagement;

